<script setup lang="ts">
import { pick } from 'es-toolkit'
import type { ButtonAsUnion, ButtonSizeUnion, UIButtonIcon, UIButtonShiny, UIIconProps } from '@core/types'
import type { NuxtLinkProps } from '#app'
import { NuxtLink, UIIcon } from '#components'
import { computed, onMounted, onUpdated, ref, shadeColor, useDefaults, useFeatureFlags, useTheme } from '#imports'
import type { ColorBaseToken, ColorValue } from '#theme'

export interface UIButtonProps extends NuxtLinkProps {
    as?: ButtonAsUnion
    size?: ButtonSizeUnion
    rounded?: boolean
    outlined?: boolean
    color?: ColorBaseToken | ColorValue | `#${string}` | false
    textColor?: ColorBaseToken | ColorValue | `#${string}`
    icon?: boolean
    block?: boolean
    elevated?: boolean
    centerText?: boolean
    disabled?: boolean
    loading?: boolean
    isLoading?: boolean
    prependIcon?: UIButtonIcon
    appendIcon?: UIButtonIcon
    shiny?: UIButtonShiny
    height?: string
    /** Wrap a non-CTA button's label for the hover glitch (CTAs are wrapped without it). Only applies when the enableCtaGlitch flag is on. */
    glitch?: boolean
}

interface ButtonStyleColors {
    '--color'?: string
    '--color-hover'?: string
    '--color-light'?: string
    '--color-dark'?: string
    '--color-glow'?: string
    '--color-shine'?: string
    '--on-color'?: string
    '--on-color-hover'?: string
    '--border-color'?: string
    '--border-color-hover'?: string
}

const _props = withDefaults(defineProps<UIButtonProps>(), {
    rounded: undefined,
    outlined: undefined,
    icon: undefined,
    block: undefined,
    elevated: undefined,
    centerText: undefined,
    disabled: undefined,
    loading: undefined,
    isLoading: undefined,
    prependIcon: undefined,
    appendIcon: undefined,
    shiny: undefined,
    glitch: undefined,
    color: undefined
})

const props = useDefaults({
    props: _props,
    defaults: {
        as: 'default',
        color: 'primary',
        rounded: true,
        outlined: false,
        icon: false,
        block: false,
        elevated: false,
        centerText: true,
        disabled: false,
        loading: false,
        isLoading: false
    }
})

const { themeClass, theme } = useTheme(() => ({
    color: props.color
}))

const flags = useFeatureFlags()
const ctaGlitchRef = ref<HTMLElement>()
const ctaGlitchText = ref('')
const isCtaGlitch = computed(() => !!flags?.enableCtaGlitch && (props.as === 'cta' || !!props.glitch))

function syncCtaGlitchText() {
    if (!isCtaGlitch.value) return
    ctaGlitchText.value = ctaGlitchRef.value?.textContent?.trim() ?? ''
}

onMounted(syncCtaGlitchText)
onUpdated(syncCtaGlitchText)

// To avoid all possible props in NuxtLinkProps to be set on component.
const allowedKeys = ['as', 'size', 'rounded', 'outlined', 'color', 'textColor', 'icon', 'block', 'elevated', 'to', 'href', 'external', 'target', 'disabled', 'height'] as const

const slots = defineSlots<{
    prepend?(): any
    default(): any
    append?(): any
}>()

function normalizeIcon(icon?: UIButtonIcon): UIIconProps | undefined {
    if (!icon) return undefined

    return typeof icon === 'string'
        ? { name: icon }
        : icon
}

const buttonIsLoading = computed(() => props.isLoading || props.loading)
const buttonIsDisabled = computed(() => props.disabled || buttonIsLoading.value)
const prependIconProps = computed(() => normalizeIcon(props.prependIcon))
const appendIconProps = computed(() => normalizeIcon(props.appendIcon))
const hasPrepend = computed(() => !!slots.prepend || !!prependIconProps.value)
const hasAppend = computed(() => !!slots.append || !!appendIconProps.value)

const buttonColorStyles = computed(() => {
    const isThemeToken = props.color && props.color in theme.value.colors

    const colorCode = isThemeToken ? theme.value.colors[props.color as keyof typeof theme.value.colors] : props.color
    let onColorCode: typeof props.color | undefined

    if (isThemeToken) {
        const onColorKey = `on-${props.color}`
        onColorCode = onColorKey in theme.value.colors ? theme.value.colors[onColorKey as keyof typeof theme.value.colors] : undefined
    }

    if (!colorCode) {
        return {}
    }

    const styles: ButtonStyleColors = {}

    if (props.as === 'cta') {
        if (!isThemeToken) {
            Object.assign(styles, {
                '--color': colorCode,
                '--color-hover': 'var(--color-light)',
                '--color-light': shadeColor(colorCode, 4),
                '--color-dark': shadeColor(colorCode, -4),
            })
        }

        Object.assign(styles, {
            '--color-glow': shadeColor(colorCode, 0, 0.35),
            '--color-shine': shadeColor(colorCode, 12),
        })
    }

    if (buttonIsDisabled.value || props.color === 'disabled') {
        styles['--color'] = 'var(--color-dark)'
        styles['--color-hover'] = 'var(--color-dark)'
        styles['--border-color-hover'] = 'var(--border-color)'
    }

    if (props.outlined || props.as === 'selectable') {
        styles['--color'] = 'var(--color-soft)'
        styles['--color-hover'] = 'var(--color-soft)'
        styles['--on-color'] = 'var(--on-color-soft)'
        styles['--on-color-hover'] = 'var(--on-color-soft)'
        styles['--border-color'] = 'var(--border-color-soft)'
    }

    if (props.icon && onColorCode) {
        if (props.color === 'background' || props.color === 'surface') {
            styles['--color'] = shadeColor(onColorCode, 12)
            styles['--color-hover'] = shadeColor(onColorCode, 16)
        }
    }

    if (props.textColor) {
        const textColorCode = props.textColor && props.textColor in theme.value.colors ? theme.value.colors[props.textColor as keyof typeof theme.value.colors] : props.textColor
        styles['--on-color'] = textColorCode
    }

    return styles
})

const buttonType = computed(() => {
    return props.href || props.to ? NuxtLink : 'button'
})
</script>

<template>
    <component
        :is="buttonType"
        v-bind="pick(props, allowedKeys)"
        :class="[
            `btn btn--${props.as}`,
            size ? `btn--${props.size}`: '',
            {
                'btn--disabled': buttonIsDisabled,
                'btn--loading': buttonIsLoading,
                'btn--rounded': props.rounded || props.icon,
                'btn--outlined': props.outlined,
                'btn--icon': props.icon,
                'btn--block': props.block,
                'btn--elevated': props.elevated,
                'btn--center-w-icon': props.centerText && (hasPrepend || hasAppend),
                'btn--shiny': props.shiny ?? true,
                'btn--shiny-snake': props.shiny === 'snake',
                ...themeClass
            }
        ]"
        :disabled="buttonIsDisabled || null"
        :aria-disabled="buttonIsDisabled || undefined"
        :aria-busy="buttonIsLoading || undefined"
        :style="[buttonColorStyles, { height: props.height }]"
    >
        <span v-if="props.shiny" class="fit-parent shine" />
        <span v-if="props.shiny" class="fit-parent shine shine--blur" />
        <div class="btn__container">
            <UILoader v-if="buttonIsLoading" :color="false" />

            <template v-else>
                <div v-if="hasPrepend" class="btn__prepend">
                    <slot name="prepend">
                        <UIIcon v-if="prependIconProps" v-bind="prependIconProps" />
                    </slot>
                </div>
                <div class="btn__content">
                    <span
                        v-if="isCtaGlitch"
                        ref="ctaGlitchRef"
                        class="ci-glitch"
                        :data-text="ctaGlitchText"
                    >
                        <slot />
                    </span>
                    <slot v-else />
                </div>
                <div v-if="hasAppend" class="btn__append">
                    <slot name="append">
                        <UIIcon v-if="appendIconProps" v-bind="appendIconProps" />
                    </slot>
                </div>
            </template>
        </div>
    </component>
</template>

<style scoped lang="scss">
@property --cta-shine-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
}

@keyframes snakeShine {
    from {
        --button-cta-shine-angle: 0deg;
    }
    to {
        --button-cta-shine-angle: 360deg;
    }
}

.btn {
    --button-cta-color-shine-shadow: #{$cta-color-shine-shadow};
    --button-cta-shine-angle: 0deg;

    display: inline-block;
    position: relative;
    padding: 10px 14px;
    border-radius: var(--border-radius, $btn-border-radius);
    outline: 0;
    user-select: none;
    text-decoration: none;
    white-space: nowrap;
    font-size: $font-size-sm;
    font-weight: $text-bold;
    text-align: center;
    transition: all 0.3s $easeOutQuad;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;

    color: var(--on-color);
    background-color: var(--color);
    border: 1px solid var(--border-color);

    &__container {
        display: grid;
        align-items: center;
        grid-template-areas: 'prepend content append';
        grid-template-columns: max-content auto max-content;
        height: 100%;
        justify-content: stretch;
        position: relative;
        width: 100%;
    }

    // Equal side columns keep the label centred on the button, not between the icons.
    &--center-w-icon &__container {
        grid-template-columns: 1fr auto 1fr;
    }

    &--center-w-icon &__append {
        justify-self: end;
    }

    &__content {
        grid-area: content;
        justify-content: center;
        display: flex;
        align-items: center;
        line-height: 1;
        gap: 6px;
    }

    &__prepend {
        grid-area: prepend;
        display: flex;
        margin-right: 4px;
    }
    &__append {
        grid-area: append;
        display: flex;
        margin-left: 4px;
    }

    &:hover,
    &:focus {
        outline: 0;
        text-decoration: none;
        background-color: var(--color-hover);
        color: var(--on-color-hover);
        border-color: var(--border-color-hover);
    }

    &:active {
        box-shadow: inset 0 2px 1px rgb(0 0 0 / 10%);
    }

    &--outlined {
        &.btn--xl {
            border-width: 2px;
        }
    }

    &--cta {
        border: 0;
        padding: 2px;
        min-height: 40px;
        contain: layout style paint;
        transition: all 0.5s $easeOutQuad;
        box-shadow: $cta-button-shadow;

        > div {
            width: 100%;
            height: 100%;
            min-height: 36px;
            padding: 8px 12px;
            border-radius: inherit;
            transition: inherit;
            pointer-events: none;

            background: var(--color);
            color: var(--on-color);
        }

        &:not(.btn--disabled) {
            &:hover,
            &:focus {
                background: var(--color-hover);
                box-shadow: $cta-button-hover-glow;
                color: var(--on-color);
                text-decoration: none;
                transition: all 0.4s $easeOutQuart;

                > div {
                    background: var(--color-hover);
                }
            }

            &:active,
            &:active:hover {
                box-shadow: var(--color);
                transform: translateY(1px);
                transition: all 0.25s $easeOutQuart;

                > div {
                    background: var(--color);
                    box-shadow: $cta-button-active-shadow;
                }

                .shine {
                    --button-cta-shine-angle: 180deg;
                    opacity: 0.2;
                    transition-duration: 0.2s;
                }
            }
        }

        &.btn--shiny {
            .shine {
                border-radius: inherit;
                background-image: (
                    conic-gradient(
                        from var(--button-cta-shine-angle),
                        transparent,
                        var(--cta-color-shine) 10%,
                        transparent 28%,
                        var(--button-cta-color-shine-shadow) 60%,
                        transparent 80%
                    )
                );
                transition: opacity ease-out 1.2s;
                will-change: opacity, filter; // iOS filter blur fix

                &--blur {
                    filter: blur(8px);
                    opacity: 0.5;

                    // iOS clips the blur at the element's edges; a no-op backdrop-filter forces
                    // the compositing path that keeps it intact. Scoped to iOS because WebKit 27.2
                    // on Linux (Playwright's mobile-safari) crashes on filter + backdrop-filter,
                    // and no other engine needs the workaround.
                    @supports (-webkit-touch-callout: none) {
                        backdrop-filter: opacity(0%);
                    }
                }
            }

            &-snake {
                .shine {
                    animation: snakeShine 8s linear infinite 0.8s;
                }

                &:active,
                &:active:hover {
                    .shine {
                        opacity: 0;
                    }
                }
            }
        }
    }

    &--link,
    &--nav {
        --border-color: transparent;
        border: unset;
        background-color: transparent;
        font-weight: $text-regular;
        color: var(--link);

        &:hover,
        &:focus {
            border: unset;
            background-color: transparent;
            text-decoration: underline;
            color: var(--on-link-hover);
        }

        &:active {
            box-shadow: unset;
        }
    }

    &--link {
        color: var(--on-link);
    }

    &--block {
        width: 100%;
    }
    &--rounded {
        border-radius: $btn-border-radius-rounded;

        .btn__container {
            border-radius: $btn-border-radius-rounded;
        }
    }

    &--xs,
    &--sm {
        min-height: 34px;
        padding: 2px;

        > div {
            min-height: 30px;
            padding: 6px 10px;
        }
    }

    &--xs {
        min-height: 26px;

        > div {
            min-height: 22px;
        }
    }

    &--xl {
        min-width: 200px;
        height: 54px;
        font-size: $font-size-base;
        border-radius: $btn-border-radius-xl;

        &.btn--cta {
            height: 60px;
            padding: 4px;
        }
    }

    &--disabled,
    fieldset[disabled] & {
        box-shadow: none;
        cursor: not-allowed;
        pointer-events: none;
        transition: none;
        opacity: 0.4;

        .btn__container {
            background: inherit !important;
        }

        .btn__append,
        .btn__content,
        .btn__prepend {
            opacity: 0.8;
        }
    }

    &--icon {
        background: transparent;
        line-height: 0;
        padding: 15px 10px;
        width: min-content;
        background: transparent;
        border: unset;
        color: var(--color);

        &:hover,
        &:focus {
            color: var(--color-hover);
            opacity: 0.9;
            background: transparent;
        }

        &.btn--sm {
            min-width: unset;
            padding: unset;

            > div {
                padding: unset;
            }
        }

        &.btn--xl {
            min-width: unset;
            width: 60px;
            padding: unset;
        }

        &:active:hover {
            background-color: transparent;
            box-shadow: none !important;
        }
    }

    &--selectable {
        font-size: $font-size-sm;
        transition: all 0.3s ease;
        border: unset;
    }

    &--selected {
        border: unset;
        color: var(--on-color, $brand-primary-color) !important;
        background: var(--color, $brand-primary) !important;
        font-size: $font-size-sm;

        &:focus,
        &:active,
        &:hover {
            border-color: var(--border-color, $brand-primary);
        }
    }

    &--nav {
        .btn__container {
            padding: 0;
        }
    }

    &:not(.btn--link):not(.btn--nav) :deep(.loader) {
        --color: var(--on-color);
    }
}
</style>
